// Signature-stable delegate (W3c). The BASE query's core — its fetch, its sort
// and its cache key — moved to `@heidi/session/data` with the session-header
// modal cores. What is left here is the module identity, the auth
// gate, and the three DERIVED wrappers.
//
// The wrappers stay because they filter through `@/utils/supportedLanguageFilters`,
// whose homing is a separate, explicitly-named decision: the `ui--session-header`
// exception rows that block on this carve ask for that module to be homed "on a
// specifier ruling in `docs/monorepo-ui-floor-rulings/`" as their other
// falsifier. A package may not import it, and this ticket does not rule on it.
//
// The auth gate is resolved here and passed in, which is ruling 32's untouched
// inner-hook contract rather than its context member: most importers of this
// hook sit outside the session surfaces (dictation settings, new-user creation,
// the Epic Haiku panel, chat) and never mounted `useCombinedQueriesTeamGeneralInfo`,
// so reading `useSessionAccessContext` here would newly fire `GET /joined-teams`
// and `GET /teams/{id}` at each of them. The value is still the
// provider-aware verdict, not `!!token`.
//
// The ratchet below fires because this file had NO session signal before the
// carve — it was a language hook — so it is absent from
// `session-ratchet-baseline.json` and acquiring the `@heidi/session` import
// reads as a new session-data file at a carved path. It is the opposite: the
// data core it named is now IN the package, and what is left is the thin
// delegate the rule's own message describes as belonging here. Suppressed
// rather than baselined because baselines are shrink-only and a row added to
// pass one's own PR is reverted.
// eslint-disable-next-line @heidi/rules/no-new-legacy-session-data -- delegate over the carved core, not new session data; the core is in packages/session/src/data
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import {
  filterSupportedLanguagesForInput,
  filterSupportedLanguagesForOutput,
} from '@/utils/supportedLanguageFilters';
import { LanguageCode } from '@heidi/schema/ml-scribe';
import { GET_SUPPORTED_LANGUAGES } from '@heidi/session/contracts';
import { useQuerySupportedLanguages as useQuerySupportedLanguagesCore } from '@heidi/session/data';

// The key declaration moved to `@heidi/session/contracts`; forwarded from the
// door (not a relative hop) so importers and `jest.mock` literals keep
// resolving against one frozen identity.
export { GET_SUPPORTED_LANGUAGES };

/**
 * `enabled` is the CALLER's own gate, ANDed with the auth verdict by the core.
 * Optional and defaulted, so every existing call site is unchanged.
 *
 * It exists because this query has no `staleTime`: where it is mounted decides
 * how often `GET /language/supported-languages` is issued. A caller that used to
 * express "only when the user asks for this" as a conditional MOUNT needs to
 * express it as a query gate once the surface reading the list takes the list as
 * a prop — see the session-header language modal's host.
 */
type SupportedLanguagesOptions = { enabled?: boolean };

export function useQuerySupportedLanguages({ enabled }: SupportedLanguagesOptions = {}) {
  const { isAuthenticated } = useIsAuthenticated();

  return useQuerySupportedLanguagesCore({ isAuthenticated, enabled });
}

export function useQueryInputSupportedLanguages(options?: SupportedLanguagesOptions) {
  const query = useQuerySupportedLanguages(options);

  return {
    ...query,
    data: query.data ? filterSupportedLanguagesForInput(query.data) : undefined,
  };
}

export function useQueryOutputSupportedLanguages(options?: SupportedLanguagesOptions) {
  const query = useQuerySupportedLanguages(options);

  return {
    ...query,
    data: query.data ? filterSupportedLanguagesForOutput(query.data) : undefined,
  };
}

const DICTATION_INPUT_EXCLUDED_LANGUAGE_CODES = new Set<LanguageCode>([
  LanguageCode.auto,
  LanguageCode.multi,
]);

/**
 * Input languages available for dictation: the input-supported list with
 * `auto` and `multi` removed. Shared by the dictation settings picker and the
 * dictate onboarding language step so both surfaces show an identical set.
 */
export function useQueryDictationInputLanguages() {
  const query = useQueryInputSupportedLanguages();

  return {
    ...query,
    data: query.data?.filter(
      (language) => !DICTATION_INPUT_EXCLUDED_LANGUAGE_CODES.has(language.code)
    ),
  };
}
